import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check, Search, Calendar } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";

// Format a Date as YYYY-MM-DD using LOCAL parts (for <input type="date"> value).
const toInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

// ── Dropdown ──────────────────────────────────────────────────────────────────
// Custom select: searchable, keyboard-friendly, animated. options:[{value,label,sub}]
export function Dropdown({ value, options, onChange, placeholder = "Select", icon, searchable, minWidth = 180 }) {
  const t = useDashTheme();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = options.find((o) => o.value === value);
  const filtered = searchable && q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;

  return (
    <div ref={ref} style={{ position: "relative", minWidth }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%", height: 36, padding: "0 12px", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 8, borderRadius: t.r.sm,
          border: `1px solid ${open ? t.c.primary : t.c.border}`, background: t.c.surface,
          color: t.c.text, fontSize: t.font.size.md, fontFamily: t.font.family, cursor: "pointer",
          boxShadow: open ? `0 0 0 3px ${t.c.primaryRing}` : "none", transition: "border-color 120ms, box-shadow 120ms",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {icon && <span style={{ color: t.c.textTertiary, display: "inline-flex" }}>{icon}</span>}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: selected ? t.c.text : t.c.textTertiary }}>
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <ChevronDown size={15} style={{ color: t.c.textTertiary, transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms" }} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }} transition={{ duration: 0.16, ease: t.motion.ease.out }}
            style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 50,
              background: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: t.r.md,
              boxShadow: t.shadow.lg, overflow: "hidden", maxHeight: 320, display: "flex", flexDirection: "column",
            }}
          >
            {searchable && (
              <div style={{ padding: 8, borderBottom: `1px solid ${t.c.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                <Search size={14} style={{ color: t.c.textTertiary }} />
                <input
                  autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..."
                  style={{ flex: 1, border: "none", outline: "none", background: "transparent", color: t.c.text, fontSize: t.font.size.md, fontFamily: t.font.family }}
                />
              </div>
            )}
            <div style={{ overflowY: "auto" }}>
              {filtered.length === 0 ? (
                <div style={{ padding: "12px 14px", fontSize: t.font.size.sm, color: t.c.textTertiary }}>No matches</div>
              ) : filtered.map((o) => {
                const active = o.value === value;
                return (
                  <button
                    key={o.value ?? "all"} onClick={() => { onChange(o.value); setOpen(false); setQ(""); }}
                    style={{
                      width: "100%", padding: "9px 12px", display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: 8, border: "none", background: active ? t.c.primarySoft : "transparent", cursor: "pointer",
                      textAlign: "left", transition: "background 100ms",
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = t.c.surfaceHover; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                  >
                    <span style={{ minWidth: 0 }}>
                      <div style={{ fontSize: t.font.size.md, color: active ? t.c.primary : t.c.text, fontWeight: active ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</div>
                      {o.sub && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{o.sub}</div>}
                    </span>
                    {active && <Check size={15} style={{ color: t.c.primary, flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── DateRangeChips ────────────────────────────────────────────────────────────
// Quick presets — Today / 7d / 30d / This month + a Custom range picker.
// Returns {start, end, key}; custom range uses key "custom".
export function DateRangeChips({ value, onChange, range, presets: presetsProp, maxDate }) {
  const t = useDashTheme();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const ref = React.useRef(null);     // anchor button wrapper
  const popRef = React.useRef(null);  // portalled popover
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  // Caller can supply its own preset chips (e.g. AI pages use yesterday-first and
  // cap the custom picker at a max date to block future selections).
  const presets = presetsProp || [
    { key: "today", label: "Today" },
    { key: "7d", label: "7 days" },
    { key: "30d", label: "30 days" },
    { key: "month", label: "This month" },
  ];

  // Position the portalled popover under the button (right-aligned), and keep it
  // on-screen. Recompute on open + on scroll/resize while open.
  const POP_W = 300;
  const place = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = r.right - POP_W;
    left = Math.max(8, Math.min(left, window.innerWidth - POP_W - 8));
    setPos({ top: r.bottom + 8, left });
  }, []);

  React.useEffect(() => {
    if (!pickerOpen) return;
    place();
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll, true); window.removeEventListener("resize", onScroll); };
  }, [pickerOpen, place]);

  React.useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Local draft for the custom picker (defaults to the current range).
  const [draftStart, setDraftStart] = React.useState(() => toInput(range?.start || new Date()));
  const [draftEnd, setDraftEnd] = React.useState(() => toInput(range?.end || new Date()));
  React.useEffect(() => {
    if (pickerOpen) {
      setDraftStart(toInput(range?.start || new Date()));
      setDraftEnd(toInput(range?.end || new Date()));
    }
  }, [pickerOpen, range]);

  const customActive = value === "custom";
  const customLabel = customActive && range
    ? `${new Date(range.start).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(range.end).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
    : "Custom";

  const applyCustom = () => {
    if (!draftStart || !draftEnd) return;
    const [sy, sm, sd] = draftStart.split("-").map(Number);
    const [ey, em, ed] = draftEnd.split("-").map(Number);
    let start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
    let end = new Date(ey, em - 1, ed, 23, 59, 59, 999);
    if (start > end) { const tmp = start; start = new Date(end); start.setHours(0, 0, 0, 0); end = new Date(tmp); end.setHours(23, 59, 59, 999); }
    onChange("custom", { start, end, key: "custom" });
    setPickerOpen(false);
  };

  const dateInputStyle = {
    height: 34, padding: "0 10px", borderRadius: t.r.sm, border: `1px solid ${t.c.border}`,
    background: t.c.surface, color: t.c.text, fontSize: t.font.size.md, fontFamily: t.font.family,
    outline: "none", colorScheme: t.isDark ? "dark" : "light",
  };

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: t.s[2] }}>
      <div style={{ display: "inline-flex", padding: 3, gap: 2, background: t.c.surfaceMuted, borderRadius: t.r.sm, border: `1px solid ${t.c.border}` }}>
        {presets.map((p) => {
          const active = value === p.key;
          return (
            <button
              key={p.key} onClick={() => onChange(p.key, presetToRange(p.key))}
              style={{
                position: "relative", height: 28, padding: "0 12px", borderRadius: 6, border: "none",
                background: "transparent", color: active ? t.c.text : t.c.textSecondary,
                fontSize: t.font.size.sm, fontWeight: active ? 600 : 500, cursor: "pointer",
                fontFamily: t.font.family, zIndex: 1, transition: "color 140ms",
              }}
            >
              {active && (
                <motion.span layoutId="dateChip" transition={t.motion.spring.snappy}
                  style={{ position: "absolute", inset: 0, background: t.c.surface, borderRadius: 6, boxShadow: t.shadow.sm, zIndex: -1 }} />
              )}
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Custom range picker */}
      <div ref={ref} style={{ position: "relative" }}>
        <button
          onClick={() => setPickerOpen((v) => !v)}
          style={{
            height: 34, padding: "0 12px", display: "inline-flex", alignItems: "center", gap: 7,
            borderRadius: t.r.sm, border: `1px solid ${customActive || pickerOpen ? t.c.primary : t.c.border}`,
            background: customActive ? t.c.primarySoft : t.c.surface,
            color: customActive ? t.c.primary : t.c.textSecondary,
            fontSize: t.font.size.sm, fontWeight: customActive ? 600 : 500, cursor: "pointer",
            fontFamily: t.font.family,
            boxShadow: pickerOpen ? `0 0 0 3px ${t.c.primaryRing}` : "none",
            transition: "border-color 120ms, box-shadow 120ms, background 120ms",
          }}
        >
          <Calendar size={14} />
          {customLabel}
          <ChevronDown size={14} style={{ transform: pickerOpen ? "rotate(180deg)" : "none", transition: "transform 160ms" }} />
        </button>
        {ReactDOM.createPortal(
          <AnimatePresence>
            {pickerOpen && (
              <motion.div
                ref={popRef}
                initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98 }} transition={{ duration: 0.16, ease: t.motion.ease.out }}
                style={{
                  position: "fixed", top: pos.top, left: pos.left, zIndex: 9999,
                  background: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: t.r.md,
                  boxShadow: t.shadow.lg, padding: t.s[4], width: POP_W, fontFamily: t.font.family,
                }}
              >
                <div style={{ fontSize: t.font.size.sm, fontWeight: 600, color: t.c.text, marginBottom: t.s[3] }}>Custom range</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: t.s[3] }}>
                  <label style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, fontWeight: 500 }}>Start date</label>
                  <input type="date" value={draftStart} max={draftEnd || maxDate || undefined} onChange={(e) => setDraftStart(e.target.value)} style={dateInputStyle} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: t.s[4] }}>
                  <label style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, fontWeight: 500 }}>End date</label>
                  <input type="date" value={draftEnd} min={draftStart || undefined} max={maxDate || undefined} onChange={(e) => setDraftEnd(e.target.value)} style={dateInputStyle} />
                </div>
                <div style={{ display: "flex", gap: t.s[2], justifyContent: "flex-end" }}>
                  <button
                    onClick={() => setPickerOpen(false)}
                    style={{ height: 34, padding: "0 14px", borderRadius: t.r.sm, border: `1px solid ${t.c.border}`, background: t.c.surface, color: t.c.textSecondary, fontSize: t.font.size.sm, fontWeight: 500, cursor: "pointer", fontFamily: t.font.family }}
                  >Cancel</button>
                  <button
                    onClick={applyCustom} disabled={!draftStart || !draftEnd}
                    style={{ height: 34, padding: "0 16px", borderRadius: t.r.sm, border: "none", background: t.c.primary, color: "#fff", fontSize: t.font.size.sm, fontWeight: 600, cursor: draftStart && draftEnd ? "pointer" : "not-allowed", opacity: draftStart && draftEnd ? 1 : 0.5, fontFamily: t.font.family }}
                  >Apply</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
      </div>
    </div>
  );
}

export function presetToRange(key) {
  const end = new Date();
  const start = new Date();
  if (key === "today") { /* same day */ }
  else if (key === "yesterday") { start.setDate(end.getDate() - 1); end.setDate(end.getDate() - 1); }
  else if (key === "7d") start.setDate(end.getDate() - 6);
  else if (key === "30d") start.setDate(end.getDate() - 29);
  else if (key === "month") start.setDate(1);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end, key };
}

// ── FilterBar — sticky container ────────────────────────────────────────────────
export function FilterBar({ left, right }) {
  const t = useDashTheme();
  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: t.s[3],
        flexWrap: "wrap", padding: `${t.s[3]}px ${t.s[4]}px`, background: t.c.surface,
        border: `1px solid ${t.c.border}`, borderRadius: t.r.lg, boxShadow: t.shadow.sm,
        position: "sticky", top: 0, zIndex: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: t.s[2], flexWrap: "wrap" }}>{left}</div>
      <div style={{ display: "flex", alignItems: "center", gap: t.s[2], flexWrap: "wrap" }}>{right}</div>
    </div>
  );
}
